import { PlusIcon } from "lucide-react";
import { reverse, sortBy } from "lodash";
import { Suspense } from "react";

import { ConnectionBridgeBaseFragment } from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import { EmptyState } from "@/components/elements/EmptyState";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { AuthorizationLinksTable } from "@/pages/Connections/AuthorizationLinksTable";
import { ApolloError } from "@apollo/client";

import { IfRole } from "@/features/roleAccess";

import { AuthorizationLinkFeatureGuard } from "./components/AuthorizationLinkFeatureGuard";
import { AuthorizationLinkTeaser } from "./components/AuthorizationLinkTeaser";

export function AuthorizationLinksTab(props: {
  connectionBridges: ConnectionBridgeBaseFragment[];
  error?: ApolloError;
  loading?: boolean;
  onCreateAuthorizationLink?: () => void;
  onDeleted?: () => void;
}) {
  // Sort connection bridges by createdAt in descending order
  const sortedConnectionBridges = reverse(
    sortBy(props.connectionBridges, "createdAt"),
  );

  if (props.error) {
    return <EmptyState header="Error" description={props.error.message} />;
  }

  return (
    <AuthorizationLinkFeatureGuard
      fallback={() => (
        <div className="mx-auto w-full max-w-lg rounded-sm border bg-popover p-8">
          <AuthorizationLinkTeaser />
        </div>
      )}
    >
      {!sortedConnectionBridges.length ? (
        <EmptyState
          header="No Authorization Links Found"
          description="Authorization Links enable to share a secure link with someone else to authorize a connection outside of your Weld account. This is useful when you don’t have direct access to the required account or data source."
          action={
            <IfRole action="createConnection">
              <Button
                onClick={props.onCreateAuthorizationLink}
                icon={<PlusIcon />}
              >
                New Authorization Link
              </Button>
            </IfRole>
          }
        />
      ) : (
        <Suspense fallback={<LoadingSpinner />}>
          <AuthorizationLinksTable
            data={sortedConnectionBridges}
            loading={props.loading}
            onDeleted={props.onDeleted}
          />
        </Suspense>
      )}
    </AuthorizationLinkFeatureGuard>
  );
}
